import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { FIDCAlert } from 'v2/components/Pages/Dashboard/FIDCAlert';
import RegistryBlockModal from 'v2/components/RegistryBlockModal';
import Footer from 'v2/components/Shared/Footer';
import GlobalWarning from 'v2/components/Shared/GlobalWarning';
import useGetPermissions from 'v2/hooks/useGetPermissions';
import { useRegistryCheck } from 'v2/hooks/useRegistryCheck';
import { useRegistrySyncUser } from 'v2/hooks/useRegistrySyncUser';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import HoustonLayout from '@eduzz/ui-layout';

import RegistryBlockAlert from '@myeduzz-vertical/root/RegistryBlockAlert';

import AppAlert from './appAlert';
import { NavBar } from './NavBar';
import RegistryBlockAlertBridge from './RegistryBlockAlertBridge';
import Toolbar from './Toolbar';

const FOOTER_HEIGHT = 53;
export const TOOLBAR_HEIGHT = 64;

interface IProps {
  children: ReactNode;
}

const Layout = ({ children }: IProps) => {
  const { pathname } = useLocation();
  const previousBaseRoute = useRef<string>();
  const [showRegistryBlockModal, setShowRegistryBlockModal] = useState(false);

  const [modules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const permissions = useGetPermissions();
  const { isSyncing: isSyncingRegistryUser } = useRegistrySyncUser();
  const { data: registryCheck, isFetching, error } = useRegistryCheck();

  const isPartner = user?.acceptence_terms;
  const showRegistryBlock = !!user && !isFetching && !error && !!registryCheck?.block && isPartner;

  useEffect(() => {
    const currentBaseRoute = pathname.replace('/producer', '').split('/')[1];

    if (showRegistryBlock && previousBaseRoute.current !== currentBaseRoute) {
      setShowRegistryBlockModal(true);
      previousBaseRoute.current = currentBaseRoute;
    }
  }, [pathname, showRegistryBlock]);

  const handleCancelRegistryBlockModal = () => {
    setShowRegistryBlockModal(false);
  };

  if (!modules || !user || isSyncingRegistryUser) return null;
  return (
    <>
      <div className='[&>div>.eduzz-ui-layout-content]:mx-auto [&>div>.eduzz-ui-layout-content]:max-w-[1672px]'>
        <HoustonLayout>
          <Toolbar />
          <NavBar />
          <HoustonLayout.Content>
            <main
              id='main-content'
              className='relative flex w-full flex-col bg-[#fcfcfc]'
              style={{ minHeight: `calc(100vh - ${FOOTER_HEIGHT + TOOLBAR_HEIGHT}px)` }}
            >
              <RegistryBlockAlertBridge>
                <RegistryBlockAlert opened={showRegistryBlock} isOwner={!!permissions?.owned} cancelable={false} />
              </RegistryBlockAlertBridge>
              <RegistryBlockModal
                opened={showRegistryBlockModal}
                isOwner={!!permissions?.owned}
                cancelable={registryCheck?.skip}
                onCancel={handleCancelRegistryBlockModal}
              />
              {isPartner && <GlobalWarning />}
              <FIDCAlert />
              {children}
              <Footer />
            </main>
          </HoustonLayout.Content>
        </HoustonLayout>
      </div>
      <AppAlert />
    </>
  );
};

export default Layout;
